import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { WINDOWS, type GroupBy, type TimelineWindow } from '@/features/timeline/domain'

const GROUPS: readonly GroupBy[] = ['empresa', 'responsable', 'ninguno']

/**
 * Los filtros del portafolio viven en la URL, no en un `useState`.
 *
 * El motivo es concreto: estas vistas se comparten por Slack ("mirá lo vencido
 * de Nimbus"). Un filtro en estado local produce un link que no muestra lo que
 * el que lo mandó estaba viendo. Ademas la URL es la unica fuente de verdad del
 * ultimo segmento de la query key, asi que dos pestañas con filtros distintos
 * no se pisan la cache.
 */

/**
 * Las tres formas de mirar la MISMA lista.
 *
 * `tablero` no es una pantalla aparte: es el portafolio agrupado por fase. Vivia
 * en `/board` con su propia query y sin un solo filtro, asi que "el tablero de
 * Nube Andina" o "lo que lleva Joaquin" no se podian ver — y eran dos vistas del
 * mismo dato que podian contradecirse. Acá hereda la barra de filtros entera.
 *
 * `/board` sigue resolviendo: redirige a `/portfolio?vista=tablero`.
 */
export type PortfolioView = 'tabla' | 'tarjetas' | 'tablero'

const VISTAS: readonly PortfolioView[] = ['tabla', 'tarjetas', 'tablero']

/**
 * De quien es el trabajo que se mira.
 *
 * `null` = **no lo dijo la URL**, asi que lo decide el rol: un `tech_member`
 * arranca en `mio` y un `admin` en `todo`. Son dos preguntas distintas y la
 * diferencia importa: si el default viviera escrito en la URL, un link compartido
 * entre un admin y un miembro mostraria cosas distintas sin decirlo. Asi, una URL
 * sin `alcance` significa "lo que le toque a quien la abra", y una con `alcance`
 * significa exactamente lo que dice.
 *
 * No es una restriccion de permisos: el `tech_member` sigue pudiendo ver todo lo
 * de sus empresas con un click. Lo que cambia es por donde empieza. La barrera
 * real sigue siendo RLS + las membresias, que no se tocan.
 */
export type PortfolioScope = 'mio' | 'todo'

/**
 * Empresa, fase y responsable son **multi-valor**: "Nube Andina o Grupo
 * Barrantes". En la URL viajan repetidos (`?empresa=a&empresa=b`), así un link
 * viejo de un solo valor sigue significando lo mismo. Vacío = sin filtro.
 *
 * Dentro de un filtro los valores van en OR; entre filtros, en AND.
 */
export interface PortfolioFilters {
  /** Ids de empresa. `[]` = todas. */
  companies: string[]
  /** `null` = lo decide el rol. Ver `PortfolioScope`. */
  scope: PortfolioScope | null
  /**
   * Cómo se agrupan los carriles de la LÍNEA DE TIEMPO.
   *
   * Vive en este hook —y no en uno propio de `timeline`— porque las dos
   * pantallas comparten el resto del vocabulario de filtros entero (empresa,
   * fase, responsable, alcance, búsqueda) y partirlo en dos gramáticas de URL
   * haría que el mismo filtro se llamara distinto en cada una. El portafolio
   * simplemente lo ignora, igual que la línea de tiempo ignora `view`.
   */
  group: GroupBy
  /** Ventana visible de la línea de tiempo. */
  window: TimelineWindow
  /**
   * Ids de columna de proyecto, de cualquier set: el backend los expande a la
   * columna equivalente (misma `key`) de cada empresa. `[]` = cualquier fase.
   */
  stages: string[]
  /** Ids de usuario y/o `sin-asignar`. `[]` = cualquiera. */
  owners: string[]
  /** Los permanentes se pueden sacar de la lista: no compiten por prioridad. */
  includeOngoing: boolean
  search: string
  view: PortfolioView
}

export const PARAM = {
  company: 'empresa',
  scope: 'alcance',
  group: 'agrupar',
  window: 'ventana',
  stage: 'fase',
  owner: 'responsable',
  ongoing: 'ongoing',
  search: 'q',
  view: 'vista',
} as const

export const DEFAULT_FILTERS: PortfolioFilters = {
  companies: [],
  scope: null,
  // Por empresa: es el corte que contesta "¿cómo viene cada cuenta?", que es la
  // pregunta de manager para la que existe la pantalla.
  group: 'empresa',
  window: 'semestre',
  stages: [],
  owners: [],
  includeOngoing: true,
  search: '',
  view: 'tabla',
}

export function parsePortfolioFilters(params: URLSearchParams): PortfolioFilters {
  const view = params.get(PARAM.view)

  const scope = params.get(PARAM.scope)
  const group = params.get(PARAM.group)
  const window = params.get(PARAM.window)

  return {
    companies: todos(params, PARAM.company),
    scope: scope === 'mio' || scope === 'todo' ? scope : null,
    group: GROUPS.includes(group as GroupBy) ? (group as GroupBy) : DEFAULT_FILTERS.group,
    window: window && window in WINDOWS ? (window as TimelineWindow) : DEFAULT_FILTERS.window,
    stages: todos(params, PARAM.stage),
    owners: todos(params, PARAM.owner),
    // Por defecto se incluyen: sacarlos es la decision explicita.
    includeOngoing: params.get(PARAM.ongoing) !== '0',
    search: params.get(PARAM.search) ?? '',
    view: VISTAS.includes(view as PortfolioView) ? (view as PortfolioView) : 'tabla',
  }
}

/** Los valores de un parámetro repetido, sin vacíos ni duplicados. */
function todos(params: URLSearchParams, nombre: string): string[] {
  return [...new Set(params.getAll(nombre).filter(Boolean))]
}

/**
 * Los filtros que cambian el resultado. Un filtro multi-valor viaja como lista;
 * el resto, como texto.
 */
export type QueryFilters = Record<string, string | string[]>

/** Solo lo que cambia el RESULTADO. La vista no filtra nada, asi que no viaja. */
export function toQueryFilters(filters: PortfolioFilters): QueryFilters {
  const query: QueryFilters = {}
  if (filters.companies.length) query[PARAM.company] = filters.companies
  // El alcance viaja tal cual; resolverlo contra el rol es cosa de la pantalla,
  // que es la unica que sabe quien esta mirando.
  if (filters.scope) query[PARAM.scope] = filters.scope
  if (filters.stages.length) query[PARAM.stage] = filters.stages
  if (filters.owners.length) query[PARAM.owner] = filters.owners
  if (!filters.includeOngoing) query[PARAM.ongoing] = '0'
  if (filters.search.trim()) query[PARAM.search] = filters.search.trim()
  return query
}

export function hasActiveFilters(filters: PortfolioFilters): boolean {
  return Object.keys(toQueryFilters(filters)).length > 0
}

export interface UsePortfolioFilters {
  filters: PortfolioFilters
  queryFilters: QueryFilters
  isFiltered: boolean
  setFilter: <K extends keyof PortfolioFilters>(key: K, value: PortfolioFilters[K]) => void
  clearFilters: () => void
}

/**
 * Qué cambios de filtro entran en la historia del navegador.
 *
 * Todos se escribían con `replace`, así que *atrás* —el gesto más automático
 * para deshacer el filtro que uno acaba de poner— saltaba la pantalla entera
 * y devolvía a la anterior. El QA funcional lo levantó como 6.2.
 *
 * No se arregla poniendo `push` en todo: **el buscador escribe una entrada
 * por tecla**, y entonces *atrás* pasaría a borrar letras de a una, que es
 * peor. Así que la regla es la intención: elegir una empresa o una fase es un
 * acto discreto y deshacerlo tiene sentido; tipear no lo es.
 *
 * `view` tampoco empuja: no es un filtro, no cambia el resultado (lo dice
 * `toQueryFilters`), y volver atrás de "tarjetas" a "tabla" no es algo que
 * nadie espere del botón de atrás.
 */
const ENTRAN_EN_LA_HISTORIA = new Set<keyof PortfolioFilters>([
  'companies',
  'scope',
  'group',
  'window',
  'stages',
  'owners',
  'includeOngoing',
])

/** Campo del modelo → nombre del parámetro, para los tres filtros multi-valor. */
const MULTI = { companies: 'company', stages: 'stage', owners: 'owner' } as const

export function usePortfolioFilters(): UsePortfolioFilters {
  const [params, setParams] = useSearchParams()
  const filters = useMemo(() => parsePortfolioFilters(params), [params])

  const setFilter = useCallback<UsePortfolioFilters['setFilter']>(
    (key, value) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)

          if (key === 'includeOngoing') {
            // Solo se escribe la desviacion del default: una URL con
            // `?ongoing=1` no dice nada y ensucia lo que se comparte.
            if (value === false) next.set(PARAM.ongoing, '0')
            else next.delete(PARAM.ongoing)
          } else if (key === 'view') {
            // Se escribe cualquier vista que NO sea la de por default, igual
            // que `includeOngoing`. Antes decía `=== 'tarjetas'` y nombraba una
            // por una: cuando el tablero se mudó acá adentro se lo agregó al
            // conmutador y al parser, pero no a esta línea, así que al tocar
            // "Tablero" el parámetro se BORRABA y la pantalla volvía a la
            // tabla. Entrar por `/portfolio?vista=tablero` funcionaba —lo que
            // fallaba era el clic— y por eso ningún test lo vio.
            if (value === 'tabla') next.delete(PARAM.view)
            else next.set(PARAM.view, String(value))
          } else if (Array.isArray(value)) {
            // Multi-valor: se reescribe entero, repitiendo el parámetro.
            const name = PARAM[MULTI[key as keyof typeof MULTI]]
            next.delete(name)
            for (const item of value) next.append(name, item)
          } else {
            const name = PARAM[key as 'scope' | 'group' | 'window' | 'search']
            if (value === null || value === '') next.delete(name)
            else next.set(name, String(value))
          }

          return next
        },
        { replace: !ENTRAN_EN_LA_HISTORIA.has(key) },
      )
    },
    [setParams],
  )

  const clearFilters = useCallback(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        next.delete(PARAM.company)
        next.delete(PARAM.stage)
        next.delete(PARAM.owner)
        next.delete(PARAM.ongoing)
        next.delete(PARAM.search)
        // La vista NO es un filtro: limpiar no te cambia de pantalla.
        return next
      },
      // Limpiar sí entra en la historia: es un acto deliberado, y *atrás*
      // tiene que poder devolver los filtros que uno acaba de tirar.
      { replace: false },
    )
  }, [setParams])

  return {
    filters,
    queryFilters: useMemo(() => toQueryFilters(filters), [filters]),
    isFiltered: hasActiveFilters(filters),
    setFilter,
    clearFilters,
  }
}
